import { CircularProgress, Pagination, PaginationItem } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { DataGrid, GridSortDirection, gridPageSelector, gridPaginationSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { RecordTableContainer, RedButton, WhiteContainer } from '../assets/style';
import { useTranslation } from "react-i18next";
export interface RecordTableProps {
    getRowId?: (x: any) => any
    columns: TableHeader[];
    rows?: TableRow[];
    checkbox?: boolean;
    color?: string;
    deleteButton?: boolean;
    onClick?: (row: TableRow, field?: string) => void; // Add the onClick prop
    onSelectRows?: (selectedRows: any[]) => void;
    selectedRows?: TableRow[];
    pages?: number
    totalCount?: number
    length?: number
    fetchDataForPage?: (pageNumber: number) => Promise<any[]>;
    updateTableRows?: (newRows: any[]) => void;
    isFetching?: boolean;
    reFetch?: any;
}

export interface TableRow {
    id: any;
    data?: Array<React.ReactNode>;
    checked?: boolean;
}

export interface TableHeader {
    field: string;
    headerName: string;
    isSortable?: boolean;
    width: number;
    renderCell?: any;
}

const RecordTableV2: React.FC<RecordTableProps> = ({
    getRowId, columns, rows, checkbox, color, deleteButton, onClick, onSelectRows,
    selectedRows, pages, totalCount, length, fetchDataForPage, updateTableRows, isFetching,
    reFetch,
}) => {
    const [currentPage, setCurrentPage] = useState(0);
    const [sortBy, setSortBy] = useState('');
    const [direction, setDirection] = useState('');
    const [deletedRows, setDeletedRows] = useState<TableRow[]>([]);
    var selectedRowData;
    const [selectionModel, setSelectionModel] = useState<any>();
    const { t, i18n } = useTranslation();

    // console.log("page ", pages)
    useEffect(() => {
        setSelectionModel(() =>
            rows
                ?.filter((r) => selectedRows?.some((row) => row.id === r.id))
                .map((r) => r.id))
    }, [rows, selectedRows, onSelectRows]);

    const handleDeleteRows: React.MouseEventHandler<HTMLButtonElement> = () => {
        //console.log("Deleted rows: ", deletedRows);
    };

    // const handleDeleteRows: React.MouseEventHandler<HTMLButtonElement> = () => {
    //     const remainingRows = rows.filter((row) => !checkedRows[row.id]);
    //     setDeletedRows(checkedRows);
    //     onDeleteRows && onDeleteRows(checkedRows);
    //     setEquipmentRows(remainingRows);
    //     setSelectedRows([]);
    // };

    useEffect(() => {
        if (reFetch != null) {
            fetchDataForPage!(currentPage + 1).then((newPageData) => updateTableRows!(newPageData));
        }
    }, [reFetch])

    if (!rows || rows.length === 0) {
        return (
            <WhiteContainer sx={{ margin: "28px", height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {t("No data available")}
            </WhiteContainer>
        );
    }
    const handleRowClick = (params: any) => {
        onClick && onClick(params.row, params?.field);
    };
    var rowCount = rows.length;
    const sortModel = sortBy && direction ? [{ field: sortBy, sort: direction as GridSortDirection }] : [];
    const CustomPagination = () => {
        const apiRef = useGridApiContext();
        const page = useGridSelector(apiRef, gridPageSelector);
        const paginationState = useGridSelector(apiRef, gridPaginationSelector);
        const currentPageSize = paginationState.paginationModel.pageSize * currentPage

        const handlePageChange = async (pageNumber: any) => {
            setCurrentPage(pageNumber - 1);// Update the current page number

            // Fetch data for the requested page using the prop function
            const newPageData = await fetchDataForPage!(pageNumber);
            updateTableRows!(newPageData); // Update the displayed data for the new page
        };
        return (
            <>
                {deleteButton && (
                    <RedButton onClick={handleDeleteRows}>Delete</RedButton>
                )}
                <Stack direction="row" spacing={2}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        {
                            currentPageSize + 1
                        } - {
                            rowCount !== 20 ?
                                totalCount : rowCount * (currentPage + 1)
                        } of {
                            totalCount
                        }
                    </Box>
                    <Pagination
                        color="primary"
                        count={pages} // Total number of pages
                        page={currentPage + 1}  // Current page number
                        onChange={(event, value) => handlePageChange(value)}
                        variant="outlined"
                        shape="rounded"
                        sx={{
                            button: { borderColor: "#70BFEC" },
                        }}
                        renderItem={(item) => (
                            <PaginationItem
                                {...item}
                                sx={{
                                    "&.Mui-selected": {
                                        backgroundColor: "#B6E2FB",
                                        color: "#0D6EFD",
                                        borderColor: "transparent",
                                        fontSize: 16,
                                        height: "32px",
                                        width: "32px",
                                        borderRadius: "8px"
                                    }
                                }}
                            />
                        )}
                    />
                </Stack>
            </>
        );
    }
    return (
        <RecordTableContainer>
            <DataGrid
                loading={(isFetching ?? false)}
                getRowId={getRowId}
                // columns={columns}
                columns={columns.map((column) => ({
                    ...column,
                    // headerClassName: "custom-header",
                    width: column.width, // Set the column width individually
                    sortable: column.isSortable,
                }))}
                rows={rows}
                // rowSelectionModel={selectedRows?.map(x => x.id)}
                // rowSelectionModel={selectedRows?.map(x => x.id) as GridRowId[]}

                onCellClick={handleRowClick}
                // onRowClick={handleRowClick}
                sx={{
                    '&>.MuiDataGrid-main': {
                        '&>.MuiDataGrid-columnHeaders': {
                            borderBottomColor: '#2C93CD',
                            borderBottomWidth: "3px",
                            background: '#012966',
                            //background: 'linear-gradient(180deg, #B6E2FB, #70BFEC)', //, #1A89C8
                        },
                    },
                    '& .MuiDataGrid-columnHeaders': { color: 'white' },
                    borderColor: 'none',
                    backgroundColor: color ?? color

                }}

                rowSelectionModel={selectionModel}
                onRowSelectionModelChange={(e) => {
                    const selectedIDs = new Set(e);
                    selectedRowData = rows
                        .filter((row) => selectedIDs.has(row.id!))
                        .map((row) => ({
                            ...row,
                            checked: true, // Set the checked property to true for the selected rows
                        }));

                    setDeletedRows(selectedRowData)
                    onSelectRows && onSelectRows(selectedRowData);
                    setSelectionModel(e);
                }}
                checkboxSelection={checkbox}
                initialState={{
                    pagination: {
                        paginationModel: { page: pages, pageSize: 20 },
                    },
                }}
                slots={{
                    pagination: CustomPagination,
                    loadingOverlay: () => <LoadingOverlay />,
                }}
                // autoHeight={true}
                sortModel={sortModel}
                disableColumnMenu
                onSortModelChange={(model) => {
                    if (model.length > 0) {
                        const { field, sort } = model[0];
                        setSortBy(field);
                        setDirection(sort as string);
                    } else {
                        setSortBy('');
                        setDirection('');
                    }
                }}
            />
        </RecordTableContainer>
    );
};

const LoadingOverlay = () => {
    const [backgroundColor, setBackgroundColor] = useState("rgba(18, 18, 18, 0)")

    useEffect(() => { setBackgroundColor("rgba(18, 18, 18, 0.2)") }, [])
    return <div style={{
        display: "flex",
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        backgroundColor: backgroundColor,
        transition: 'background-color 0.5s ease-in-out',
    }}>
        <CircularProgress />
    </div>
}
export default RecordTableV2;
